import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { removeWillChangeOnAnimationEnd } from '@theme/utilities';

/** Main menu drawer; retains the theme's public open/toggle/back/close contracts. */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #listeners;
  #motion;
  #motionTimer;
  #accordionTimer;
  #frame;
  #ownsFocus = false;
  #version = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const signal = this.#listeners.signal;
    this.addEventListener('keyup', this.#onKeyUp, { signal });
    this.querySelectorAll('.menu-drawer__animated-element').forEach(element => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd, { signal });
    });
    this.querySelectorAll('.menu-drawer__submenu').forEach(panel => {
      const expanded = !!panel.closest('details')?.classList.contains('menu-open');
      panel.inert = !expanded;
      panel.setAttribute('aria-hidden', String(!expanded));
    });
    this.refs.menuDrawer.inert = !this.isOpen;
    this.refs.menuDrawer.setAttribute('aria-hidden', String(!this.isOpen));
    this.refs.details.querySelector('summary')?.setAttribute('aria-expanded', String(this.isOpen));
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#cancelMotion();
    clearTimeout(this.#accordionTimer);
    this.#restoreAccordions();
    if (this.#ownsFocus) { removeTrapFocus(); this.#ownsFocus = false; }
    super.disconnectedCallback();
  }

  get isOpen() { return this.refs.details.hasAttribute('open'); }

  #onKeyUp = event => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.stopPropagation();
    this.#close(this.#getDetailsElement(event));
  };

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    const details = event.target.closest('details');
    return details && this.contains(details) ? details : this.refs.details;
  }

  toggle(event) {
    event?.preventDefault?.();
    return this.isOpen ? this.close() : this.open();
  }

  open(target, event) {
    event?.preventDefault?.();
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;
    this.#cancelMotion();
    const version = this.#version;
    summary.setAttribute('aria-expanded', 'true');
    this.refs.menuDrawer.inert = false;
    this.refs.menuDrawer.setAttribute('aria-hidden', 'false');
    this.preventInitialAccordionAnimations(details);
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      if (!this.isConnected || version !== this.#version) return;
      // Native summary default action has run by now; also support programmatic open().
      details.setAttribute('open', '');
      details.classList.add('menu-open');
      if (target) this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      const panel = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      if (panel) { panel.inert = false; panel.setAttribute('aria-hidden', 'false'); }
      this.#afterMotion(panel || details, () => {
        if (!this.isConnected || !details.open || !details.classList.contains('menu-open')) return;
        trapFocus(panel || details);
        this.#ownsFocus = true;
      });
    });
  }

  back(event) { this.#close(this.#getDetailsElement(event)); }
  close() { this.#close(this.refs.details); }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;
    this.#cancelMotion();
    summary.setAttribute('aria-expanded', 'false');
    if (!details.open && !details.classList.contains('menu-open')) {
      if (details === this.refs.details) {
        this.refs.menuDrawer.inert = true;
        this.refs.menuDrawer.setAttribute('aria-hidden', 'true');
      }
      return;
    }
    details.classList.remove('menu-open');
    const isRoot = details === this.refs.details;
    const panel = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    if (isRoot || details.parentElement?.closest('details') === this.refs.details) {
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    }
    // Keep current focus until the closing panel is reset; do not inert a focused subtree.
    this.#afterMotion(panel || details, () => {
      reset(details);
      if (isRoot) {
        if (this.#ownsFocus) { removeTrapFocus(); this.#ownsFocus = false; }
        this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
        summary.focus({ preventScroll: true });
        this.refs.menuDrawer.inert = true;
        this.refs.menuDrawer.setAttribute('aria-hidden', 'true');
      } else {
        const parent = details.parentElement?.closest('details') || this.refs.details;
        const parentPanel = parent.querySelector('.menu-drawer, .menu-drawer__submenu');
        trapFocus(parentPanel || parent);
        this.#ownsFocus = true;
        summary.focus({ preventScroll: true });
        if (panel) { panel.inert = true; panel.setAttribute('aria-hidden', 'true'); }
      }
    });
  }

  #cancelMotion() {
    this.#version++;
    this.#motion?.abort();
    clearTimeout(this.#motionTimer);
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  #afterMotion(element, callback) {
    this.#motion?.abort();
    clearTimeout(this.#motionTimer);
    const controller = new AbortController();
    this.#motion = controller;
    const version = this.#version;
    const style = getComputedStyle(element);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const duration = reduced ? 0 : Math.min(1200, Math.max(
      longestMotion(style.transitionDuration, style.transitionDelay),
      longestMotion(style.animationDuration, style.animationDelay)
    ));
    const started = performance.now();
    let done = false;
    const finish = () => {
      if (done || controller.signal.aborted || version !== this.#version) return;
      done = true;
      controller.abort();
      clearTimeout(this.#motionTimer);
      callback();
    };
    const ended = event => {
      if (event.target === element && performance.now() - started >= duration - 20) finish();
    };
    element.addEventListener('transitionend', ended, { signal: controller.signal });
    element.addEventListener('animationend', ended, { signal: controller.signal });
    // A finite fallback also covers zero-motion, interrupted transitions and detached CSS.
    this.#motionTimer = setTimeout(finish, duration ? duration + 50 : 0);
  }

  preventInitialAccordionAnimations(details) {
    clearTimeout(this.#accordionTimer);
    this.#restoreAccordions();
    details.querySelectorAll('accordion-custom .details-content').forEach(element => {
      element.classList.add('details-content--no-animation');
    });
    this.#accordionTimer = setTimeout(() => this.#restoreAccordions(), 100);
  }

  #restoreAccordions() {
    this.querySelectorAll('.details-content--no-animation').forEach(element => {
      element.classList.remove('details-content--no-animation');
    });
  }
}

function longestMotion(durations = '0s', delays = '0s') {
  const parse = value => value.split(',').map(part => {
    const text = part.trim();
    return (parseFloat(text) || 0) * (text.endsWith('ms') ? 1 : 1000);
  });
  const times = parse(durations), offsets = parse(delays);
  return Math.max(0, ...times.map((time, index) => time + offsets[index % offsets.length]));
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  const panel = element.querySelector('.menu-drawer__submenu');
  if (panel) { panel.inert = true; panel.setAttribute('aria-hidden', 'true'); }
}

if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);
